import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import {
  getCompanyAnalyticsSettings,
  updateCompanyAnalyticsSettings,
  claimLegacyStorageItem,
} from "services/firebase/firebase.service";
import {
  type AnalyticsPeriod,
  getWeekBounds,
  isDateInPeriod,
} from "./analytics.operatingHours";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  SaveErrorText,
  EmptyNote,
  DeleteBtn,
  AddSection,
  AddSectionTitle,
  FormField,
  FormLabel,
  FormInput,
  FormRow,
  DatePickerWrapper,
  ScrollArea,
  SalesList,
  Splitter,
  SaleRow,
  SaleInfo,
  SaleTitle,
  SaleMeta,
  ReadOnlyDateLabel,
  WeekGrid,
  WeekRow,
  WeekDayLabel,
  WeekCurrentLabel,
  WeekAmountField,
  ClearButtonSlot,
  WeekAddSection,
  ImportLink,
} from "./analytics.customSales.styles";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomSale {
  id: string;
  /** Optional — an unnamed sale is still valid, just shown without a title. */
  title: string;
  amount: number;
  /** ISO date string "YYYY-MM-DD" — the specific calendar day this sale belongs to */
  date: string;
}

// ─── Storage ──────────────────────────────────────────────────────────────────
//
// Lives in Firebase Storage under Company/{companyId}/analytics/custom-sales
// — the same "realm" as the company photo, just its own subfolder. The legacy
// localStorage keys below are read-only now: consulted only as a one-time
// migration source, then never written to again.

// Custom Sales' own key, from before this moved off-device.
const LEGACY_STORAGE_KEY = "analytics_custom_sales";
// Custom Sales replaced the separate, now-removed "Custom Earnings" feature
// — identical shape ({id, title, amount, date}), same purpose (manually
// recorded revenue the API doesn't capture) — which used its own legacy key.
// Checked too so companies who only ever used Custom Earnings don't have
// that history silently orphaned.
const LEGACY_EARNINGS_STORAGE_KEY = "analytics_custom_earnings";
const ANALYTICS_SETTINGS_KEY = "custom-sales";

const isCustomSale = (value: unknown): value is CustomSale => {
  if (typeof value !== "object" || value === null) return false;
  const s = value as Record<string, unknown>;
  return (
    typeof s.id === "string" &&
    typeof s.title === "string" &&
    typeof s.amount === "number" &&
    typeof s.date === "string"
  );
};

/** Type guard for the Firebase-stored value — see getCompanyAnalyticsSettings. */
const isCustomSaleArray = (value: unknown): value is CustomSale[] =>
  Array.isArray(value) && value.every(isCustomSale);

const parseStoredCustomSales = (raw: string | null): CustomSale[] | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed))
      return parsed.map(
        (s): CustomSale => ({
          id: s.id,
          title: s.title,
          amount: s.amount,
          date: s.date,
        }),
      );
  } catch {}
  return null;
};

export const loadCustomSales = async (
  companyId: number,
): Promise<CustomSale[]> => {
  const remote = await getCompanyAnalyticsSettings<CustomSale[]>(
    companyId,
    ANALYTICS_SETTINGS_KEY,
    isCustomSaleArray,
  );
  if (remote) return remote;

  // One-time migration. Both legacy keys are global — not scoped per
  // company — so claiming them (read + remove, synchronously, no await in
  // between) must happen before this call awaits anything. Otherwise a
  // second company's load starting while this one is still mid-write could
  // read the same still-present key(s) and wrongly adopt this company's
  // data as its own. Both sources are merged (not one-or-the-other) so a
  // company that used both Custom Sales and, previously, Custom Earnings
  // keeps every entry. If the Firebase write below fails, both raw values
  // are restored so the next load can retry instead of losing them.
  const claimedSales = claimLegacyStorageItem(LEGACY_STORAGE_KEY);
  const claimedEarnings = claimLegacyStorageItem(LEGACY_EARNINGS_STORAGE_KEY);
  const legacy = [
    ...(claimedSales ? (parseStoredCustomSales(claimedSales.raw) ?? []) : []),
    ...(claimedEarnings
      ? (parseStoredCustomSales(claimedEarnings.raw) ?? [])
      : []),
  ];
  if (legacy.length > 0) {
    try {
      await updateCompanyAnalyticsSettings(
        companyId,
        ANALYTICS_SETTINGS_KEY,
        legacy,
      );
    } catch {
      claimedSales?.restore();
      claimedEarnings?.restore();
    }
    return legacy;
  }

  return [];
};

export const saveCustomSales = (
  companyId: number,
  sales: CustomSale[],
): Promise<void> =>
  updateCompanyAnalyticsSettings(companyId, ANALYTICS_SETTINGS_KEY, sales);

// ─── Period-aware filtering ───────────────────────────────────────────────────
//
// isDateInPeriod lives in analytics.operatingHours.tsx, shared with
// analytics.customCosts.tsx.

/** Returns only the sales whose date falls within the selected analytics period. */
export const filterCustomSalesForPeriod = (
  sales: CustomSale[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): CustomSale[] =>
  sales.filter((s) => isDateInPeriod(s.date, period, selectedDate));

/** Returns the total custom sales active for the selected analytics period. */
export const getCustomSalesTotal = (
  sales: CustomSale[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number =>
  filterCustomSalesForPeriod(sales, period, selectedDate).reduce(
    (sum, s) => sum + s.amount,
    0,
  );

// ─── Grouping ─────────────────────────────────────────────────────────────────

interface SaleGroup {
  date: string;
  total: number;
  /** Distinct, non-empty titles rolled into this date — joined for display. */
  titles: string[];
  /** ids of every underlying entry merged into this group, for bulk delete. */
  ids: string[];
}

/**
 * Collapses multiple sales on the same date into a single summarized entry
 * (amounts summed, titles combined) instead of listing each one separately.
 * Applies uniformly across day/week/month/year — a date only ever shows one row.
 */
const groupSalesByDate = (sales: CustomSale[]): SaleGroup[] => {
  const map = new Map<string, SaleGroup>();
  for (const sale of sales) {
    const title = sale.title.trim();
    const group = map.get(sale.date);
    if (group) {
      group.total += sale.amount;
      group.ids.push(sale.id);
      if (title && !group.titles.includes(title)) group.titles.push(title);
    } else {
      map.set(sale.date, {
        date: sale.date,
        total: sale.amount,
        titles: title ? [title] : [],
        ids: [sale.id],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
};

// ─── Side pane ────────────────────────────────────────────────────────────────

interface CustomSalesPaneProps {
  initial: CustomSale[];
  open: boolean;
  companyId: number;
  /** Drives the add-form layout: fixed date label (day), week quick-fill grid
   * (week), or a free date picker (month/year). */
  period: AnalyticsPeriod;
  /** Pre-fills the date field when adding a new sale */
  selectedDate: LocalDate;
  onSave: (sales: CustomSale[]) => void;
  onClose: () => void;
  /** Opens the CSV import pane. Omitted entirely if the caller doesn't wire it. */
  onImport?: () => void;
}

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const formatDate = (d: LocalDate): string =>
  `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}/${d.year()}`;

const WEEKDAY_SHORT_KEYS = [
  "WeekDays.MONDAYSHORT",
  "WeekDays.TUESDAYSHORT",
  "WeekDays.WEDNESDAYSHORT",
  "WeekDays.THURSDAYSHORT",
  "WeekDays.FRIDAYSHORT",
  "WeekDays.SATURDAYSHORT",
  "WeekDays.SUNDAYSHORT",
];

const MIN_LIST_HEIGHT = 60;
// Reserve roughly this much vertical space for everything else in the pane
// (header, description, add-section, footer) when computing the max height
// the list can be dragged to.
const RESERVED_CHROME_HEIGHT = 320;

export const CustomSalesPane = (props: CustomSalesPaneProps) => {
  const { t } = useTranslation();
  const [sales, setSales] = useState<CustomSale[]>(props.initial);
  const [newTitle, setNewTitle] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newDate, setNewDate] = useState<LocalDate>(props.selectedDate);
  const [weekAmounts, setWeekAmounts] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Drag-to-resize the sales list vs. the add-sale form below it.
  // `listHeight === null` means "let flex decide" (the default).
  const [listHeight, setListHeight] = useState<number | null>(null);
  const [isDraggingSplitter, setIsDraggingSplitter] = useState(false);
  const salesListRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ startY: number; startHeight: number } | null>(
    null,
  );

  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setSales(props.initial);
      setNewTitle("");
      setNewAmount("");
      setNewDate(props.selectedDate);
      setWeekAmounts({});
      setListHeight(null);
      setSaveError(false);
    }
    wasOpen.current = props.open;
  }, [props.open, props.initial, props.selectedDate]);

  const handleSplitterMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = {
      startY: e.clientY,
      startHeight: salesListRef.current?.getBoundingClientRect().height ?? 200,
    };
    setIsDraggingSplitter(true);
  };

  useEffect(() => {
    if (!isDraggingSplitter) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStateRef.current) return;
      const delta = e.clientY - dragStateRef.current.startY;
      const maxHeight = Math.max(
        MIN_LIST_HEIGHT,
        window.innerHeight - RESERVED_CHROME_HEIGHT,
      );
      const next = dragStateRef.current.startHeight + delta;
      setListHeight(Math.min(Math.max(next, MIN_LIST_HEIGHT), maxHeight));
    };
    const handleMouseUp = () => {
      setIsDraggingSplitter(false);
      dragStateRef.current = null;
    };
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingSplitter]);

  // Week view: one amount field per weekday, so the whole week can be filled
  // in without opening a date picker for each entry.
  const { weekStart } = getWeekBounds(props.selectedDate);
  const weekDates = Array.from({ length: 7 }, (_, i) => weekStart.plusDays(i));

  // Day/month/year: reads whatever is currently typed in the add-form and
  // turns it into a new entry — without touching state. There's no separate
  // "Add" step anymore: Save both commits the form and persists/closes.
  //  - Day view       : date is fixed to the selected day, not user-editable.
  //  - Month/year view: date comes from the free-form DatePicker (newDate).
  const buildPendingAddition = (): CustomSale | null => {
    const amount = parseFloat(newAmount.replace(",", "."));
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const date = props.period === "day" ? props.selectedDate : newDate;
    return {
      id: generateId(),
      title: newTitle.trim(),
      amount,
      date: date.toString(),
    };
  };

  // Week: for every day whose field holds a valid amount, REPLACE all of
  // that day's committed entries with a single one — not add on top. This is
  // what makes "click the current total to load it, then adjust it" work:
  // without replace semantics, re-saving a loaded value would double it.
  // An explicit 0 clears the day entirely (no replacement entry is created).
  // Days left untouched (empty field) keep whatever entries they already had.
  const applyWeekEdits = (committed: CustomSale[]): CustomSale[] => {
    const edits = new Map<string, number>();
    for (const date of weekDates) {
      const ds = date.toString();
      const raw = weekAmounts[ds];
      if (raw === undefined || raw === "") continue; // untouched
      const amount = parseFloat(raw.replace(",", "."));
      if (!Number.isFinite(amount) || amount < 0) continue; // invalid, ignore
      edits.set(ds, amount);
    }
    if (edits.size === 0) return committed;
    const untouched = committed.filter((s) => !edits.has(s.date));
    const replacements: CustomSale[] = Array.from(edits.entries())
      .filter(([, amount]) => amount > 0)
      .map(([date, amount]) => ({
        id: generateId(),
        title: newTitle.trim(),
        amount,
        date,
      }));
    return [...untouched, ...replacements];
  };

  // Loads a day's current total into its own input, so editing starts from
  // what's already recorded instead of from scratch. Rounded to the nearest
  // öre (2 decimals), NOT the nearest whole krona: applyWeekEdits treats
  // this field as a real edit and replaces the day's entries with whatever
  // it holds on Save, so rounding to a whole number here would silently
  // drop cents from a fractional total (e.g. a CSV-imported 1234.75 kr)
  // even if the user never touched the field after clicking to load it.
  const handleUseCurrentAsInput = (dateStr: string, amount: number) =>
    setWeekAmounts((prev) => ({
      ...prev,
      [dateStr]: String(Math.round(amount * 100) / 100),
    }));

  // Marks a day's total for clearing (an explicit 0) — takes effect on Save.
  const handleClearDay = (dateStr: string) =>
    setWeekAmounts((prev) => ({ ...prev, [dateStr]: "0" }));

  // Deleting a summarized row removes every entry merged into it.
  // Note: this only removes committed entries — a pending, not-yet-saved
  // amount typed into the form is cleared by editing the field itself.
  const handleDeleteGroup = (ids: string[]) =>
    setSales((prev) => prev.filter((s) => !ids.includes(s.id)));

  // Live preview: committed sales plus whatever is currently typed into the
  // add-form, recomputed on every keystroke so the list and the week grid's
  // per-day totals update as you type — without waiting for Save. Week uses
  // replace semantics (see applyWeekEdits); day/month/year stay additive.
  const pendingAddition =
    props.period === "week" ? null : buildPendingAddition();
  const previewSales =
    props.period === "week"
      ? applyWeekEdits(sales)
      : pendingAddition
        ? [...sales, pendingAddition]
        : sales;

  // The list only shows sales for the currently selected period — a sale
  // added on Aug 10 shouldn't clutter the list while Aug 13 (or the week of
  // Aug 10–16 vs. a different week) is selected. Deleting/saving still
  // operates on the full `sales` array, so nothing outside view is lost.
  const visibleSales = filterCustomSalesForPeriod(
    previewSales,
    props.period,
    props.selectedDate,
  );
  const visibleGroups = groupSalesByDate(visibleSales);

  // Per-day totals (committed + pending) shown in the week grid so each row
  // reflects the running total live as its own amount field is edited.
  const groupedByDate = new Map(
    groupSalesByDate(previewSales).map((g) => [g.date, g]),
  );

  // previewSales already reflects the correct semantics for the current
  // period (replace for week, additive for day/month/year). It's the exact
  // same reference as `sales` when nothing's currently pending in the
  // add-form/week grid (see applyWeekEdits/pendingAddition above), which
  // handleImport below relies on to skip an unnecessary save.
  const persist = (onDone?: () => void) => {
    setIsSaving(true);
    setSaveError(false);
    saveCustomSales(props.companyId, previewSales)
      .then(() => {
        setIsSaving(false);
        props.onSave(previewSales);
        onDone?.();
      })
      .catch(() => {
        setIsSaving(false);
        setSaveError(true);
      });
  };

  const handleSave = () => persist();

  // "Import from CSV" used to switch panes immediately, silently discarding
  // whatever was currently typed into the add-form or week grid but not yet
  // saved. Persist it first — same as clicking Save — before opening the
  // import pane, unless there's nothing pending (skip the round-trip then).
  const handleImport = () => {
    if (!props.onImport) return;
    if (previewSales === sales) {
      props.onImport();
      return;
    }
    persist(props.onImport);
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.CustomSales")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.CustomSalesDescription")}
        </PaneDescription>
        {props.onImport && (
          <ImportLink type="button" onClick={handleImport} disabled={isSaving}>
            {t("CompanyAnalytics.ImportFromCsv")}
          </ImportLink>
        )}

        {/* Scrollable so the add-form and its buttons stay reachable even
            when the list plus (in week view) the day grid grow tall. */}
        <ScrollArea>
          {/* ── Existing sales list (scoped to the selected period,
                summarized to one row per date, live as you type). Hidden in
                week view — the day grid's per-day totals already show this. ── */}
          {props.period !== "week" && (
            <>
              <SalesList ref={salesListRef} $height={listHeight}>
                {visibleGroups.length === 0 ? (
                  <EmptyNote>{t("CompanyAnalytics.NoCustomSales")}</EmptyNote>
                ) : (
                  visibleGroups.map((group) => (
                    <SaleRow key={group.date}>
                      <SaleInfo>
                        {group.titles.length > 0 && (
                          <SaleTitle>{group.titles.join(", ")}</SaleTitle>
                        )}
                        <SaleMeta>
                          {Math.round(group.total).toLocaleString("sv-SE")} kr
                          {" · "}
                          {formatDate(LocalDate.parse(group.date))}
                        </SaleMeta>
                      </SaleInfo>
                      <DeleteBtn
                        onClick={() => handleDeleteGroup(group.ids)}
                        aria-label={t("CompanyAnalytics.DeleteSale")}
                      >
                        ✕
                      </DeleteBtn>
                    </SaleRow>
                  ))
                )}
              </SalesList>

              <Splitter
                $dragging={isDraggingSplitter}
                onMouseDown={handleSplitterMouseDown}
                role="separator"
                aria-orientation="horizontal"
                aria-label={t("CompanyAnalytics.ResizeSalesList")}
              />
            </>
          )}

          {/* ── Add new sale form ── */}
          {props.period === "week" ? (
            // Stretched to fill whatever's left of ScrollArea so the day
            // grid below can grow into the remaining space instead of
            // leaving a gap above the footer.
            <WeekAddSection>
              <AddSectionTitle>{t("CompanyAnalytics.AddSale")}</AddSectionTitle>
              <WeekGrid>
                {weekDates.map((date, i) => {
                  const current = groupedByDate.get(date.toString());
                  return (
                    <WeekRow key={date.toString()}>
                      <WeekDayLabel>
                        {t(WEEKDAY_SHORT_KEYS[i])}{" "}
                        {String(date.dayOfMonth()).padStart(2, "0")}/
                        {String(date.monthValue()).padStart(2, "0")}
                      </WeekDayLabel>
                      <WeekCurrentLabel
                        type="button"
                        $clickable={!!current}
                        disabled={!current}
                        title={
                          current
                            ? t("CompanyAnalytics.ClickToEditAmount")
                            : undefined
                        }
                        onClick={() =>
                          current &&
                          handleUseCurrentAsInput(
                            date.toString(),
                            current.total,
                          )
                        }
                      >
                        {current
                          ? `${Math.round(current.total).toLocaleString("sv-SE")} kr`
                          : "–"}
                      </WeekCurrentLabel>
                      <WeekAmountField>
                        <FormInput
                          type="number"
                          value={weekAmounts[date.toString()] ?? ""}
                          placeholder="0"
                          min="0"
                          onChange={(e) =>
                            setWeekAmounts((prev) => ({
                              ...prev,
                              [date.toString()]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSave()}
                        />
                      </WeekAmountField>
                      {/* Placed after the input, not next to the sum, so the
                          "×" can't be misread as multiplying the two. */}
                      <ClearButtonSlot>
                        {current && (
                          <DeleteBtn
                            type="button"
                            onClick={() => handleClearDay(date.toString())}
                            aria-label={t("CompanyAnalytics.ClearAmount")}
                            title={t("CompanyAnalytics.ClearAmount")}
                          >
                            ✕
                          </DeleteBtn>
                        )}
                      </ClearButtonSlot>
                    </WeekRow>
                  );
                })}
              </WeekGrid>
            </WeekAddSection>
          ) : (
            <AddSection>
              <AddSectionTitle>{t("CompanyAnalytics.AddSale")}</AddSectionTitle>
              <FormField>
                <FormLabel>{t("CompanyAnalytics.SaleTitle")}</FormLabel>
                <FormInput
                  type="text"
                  value={newTitle}
                  placeholder={t("CompanyAnalytics.SaleTitlePlaceholder")}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
              </FormField>
              <FormRow>
                <FormField>
                  <FormLabel>{t("CompanyAnalytics.CostAmount")}</FormLabel>
                  <FormInput
                    type="number"
                    value={newAmount}
                    placeholder="0"
                    min="0"
                    onChange={(e) => setNewAmount(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  />
                </FormField>
                <FormField>
                  <FormLabel>{t("CompanyAnalytics.SaleDate")}</FormLabel>
                  {props.period === "day" ? (
                    <ReadOnlyDateLabel>
                      {formatDate(props.selectedDate)}
                    </ReadOnlyDateLabel>
                  ) : (
                    <DatePickerWrapper>
                      <DatePicker
                        value={newDate}
                        onChange={(date) => {
                          if (date) setNewDate(date);
                        }}
                        size="compact"
                        dateFormat="DD/MM/YYYY"
                      />
                    </DatePickerWrapper>
                  )}
                </FormField>
              </FormRow>
            </AddSection>
          )}
        </ScrollArea>

        <PaneFooter>
          {saveError && (
            <SaveErrorText>{t("CompanyAnalytics.SaveError")}</SaveErrorText>
          )}
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
            disabled={isSaving}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.Save")}
            onClick={handleSave}
            disabled={isSaving}
            isLoading={isSaving}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
